<script setup lang="ts">
import { GEO_HUB_MIN_CITY } from "#shared/utils/geoHubThresholds";
import { nomDepuisCleVille } from "#shared/utils/villeAnnuaire";

/**
 * Page programmatique SEO : cagnottes par ville (longue traîne géo).
 * Ex : /cagnottes/lyon — ranke sur "cagnotte lyon", "cagnotte association lyon".
 *
 * Karim 2026-06-15 (SEO sprint v4).
 *
 * Pattern :
 * - SSR via useAsyncData (fetch côté server → indexable Google)
 * - noindex si 0 cagnottes (anti thin-content)
 * - JSON-LD CollectionPage + ItemList + BreadcrumbList
 * - Internal links vers /associations/ville/[ville] (cross-link maillage)
 */
import { formatCurrency } from "@donoor/utils";

const { t } = useI18n({ useScope: "local" });

const config = useRuntimeConfig();
const apiUrl = config.public.apiBaseUrl;
const assetsUrl = `${apiUrl}/api/assets`;

const route = useRoute();
const villeSlug = (route.params as Record<string, string | string[] | undefined>).ville as string;

interface CampaignWithCity {
  id: number;
  title: string;
  slug: string;
  image: string | null;
  goal_amount: number;
  currency: string;
  raised: number;
  donors_count: number;
  percent: number;
  organization_name: string;
  organization_slug: string | null;
  organization_logo: string | null;
  city_slug: string;
  city_name: string;
}

const { data, pending } = await useAsyncData(
  `campaigns-city-${villeSlug}`,
  async () => {
    try {
      return await $fetch<{
        cities: Array<{ slug: string; name: string; count: number }>;
        campaigns: CampaignWithCity[];
        cityName: string | null;
        redirection?: string | null;
      }>(`/api/campaigns/by-city?city=${villeSlug}`);
    } catch {
      return { cities: [], campaigns: [], cityName: null, redirection: null };
    }
  },
  { server: true, lazy: false },
);

// Ancienne clé de ville (lot 4 : suffixe d'outre-mer ajouté, commune
// renommée par la BAN) : 301 vers la nouvelle clé, même hub.
if (data.value?.redirection) {
  await navigateTo(`/cagnottes/${data.value.redirection}`, { redirectCode: 301 });
}

const campaigns = computed(() => data.value?.campaigns || []);
const cityName = computed(() => {
  if (data.value?.cityName) return data.value.cityName;
  return nomDepuisCleVille(villeSlug);
});

const canonicalUrl = `https://donoor.org/cagnottes/${villeSlug}`;

// Pas de "| Donoor" suffix : nuxt.config.ts titleTemplate "%s"
// le rajoute auto. Sinon on aurait "X | Donoor".
const seoTitle = computed(
  () => t('seo.title', { ville: cityName.value }),
);
const seoDescription = computed(
  () =>
    t('seo.description', {
      nombre: campaigns.value.length,
      cagnottes: campaigns.value.length > 1 ? t('mot.cagnottes') : t('mot.cagnotte'),
      ville: cityName.value,
    }),
);

const shouldNoindex = computed(
  () => !pending.value && campaigns.value.length < GEO_HUB_MIN_CITY,
);
// robots cohérent avec l'indexabilité globale (staging = noindex) + noindex page si contenu mince.
const robotsContent = useRobotsContent(shouldNoindex);

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogImage: () => `https://donoor.org/api/og/recherche?type=cagnottes&count=${campaigns.value.length}`,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogUrl: canonicalUrl,
  ogLocale: "fr_FR",
  twitterCard: "summary_large_image",
  robots: () => robotsContent.value,
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  meta: computed(() => [{ name: "robots", content: robotsContent.value }]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoTitle.value,
        description: seoDescription.value,
        url: canonicalUrl,
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: t('breadcrumb.accueil'), item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: t('breadcrumb.cagnottes'), item: "https://donoor.org/recherche/cagnottes" },
            { "@type": "ListItem", position: 3, name: cityName.value, item: canonicalUrl },
          ],
        },
        ...(campaigns.value.length > 0 && {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: campaigns.value.length,
            itemListElement: campaigns.value.slice(0, 20).map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "DonateAction",
                name: c.title,
                url: `https://donoor.org/c/${c.slug}`,
                agent: { "@type": "Organization", name: c.organization_name },
              },
            })),
          },
        }),
      }),
    },
  ]),
});

const getImageUrl = (fileId: string | null) =>
  fileId ? `${assetsUrl}/${fileId}?width=600&height=400&fit=cover&format=webp&quality=80` : null;
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Hero unifié (même UX que /cagnottes) avec ville pré-filtrée -->
    <RechercheUnifiedHero
      active-type="cagnottes"
      :h1="t('hero.h1', { ville: cityName })"
      :intro="t('hero.intro', { nombre: campaigns.length, cagnottes: campaigns.length > 1 ? t('mot.cagnottes') : t('mot.cagnotte'), actives: campaigns.length > 1 ? t('mot.actives') : t('mot.active'), ville: cityName })"
      :ville-slug="villeSlug"
      :ville-name="cityName"
    />

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
      <!-- Loading -->
      <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div v-for="i in 6" :key="i" class="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
          <div class="h-44 bg-gray-200" />
          <div class="p-4 space-y-2">
            <div class="h-4 w-3/4 bg-gray-200 rounded" />
            <div class="h-3 w-1/2 bg-gray-100 rounded" />
          </div>
        </div>
      </div>

      <!-- List -->
      <div v-else-if="campaigns.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <NuxtLink
          v-for="c in campaigns"
          :key="c.id"
          :to="`/c/${c.slug}`"
          :aria-label="t('liste.ariaCagnotte', { titre: c.title, association: c.organization_name, ville: cityName })"
          class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 no-underline"
        >
          <div class="relative h-44 overflow-hidden bg-gray-100">
            <img
              v-if="c.image"
              :src="getImageUrl(c.image)!"
              :alt="t('liste.altImage', { titre: c.title })"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
              <UIcon name="i-lucide-image" class="size-10" />
            </div>
          </div>
          <div class="p-4">
            <div v-if="c.organization_name" class="flex items-center gap-2 mb-2">
              <img
                v-if="c.organization_logo"
                :src="`${assetsUrl}/${c.organization_logo}?width=40&height=40&fit=contain&format=webp&quality=80`"
                :alt="t('liste.altLogo', { association: c.organization_name })"
                class="size-5 rounded-full object-contain bg-white border border-gray-100"
                loading="lazy"
              />
              <span class="text-xs text-gray-500 truncate">{{ c.organization_name }}</span>
            </div>
            <h2 class="font-semibold text-gray-900 text-sm sm:text-base leading-snug line-clamp-2 group-hover:text-(--color-main) transition-colors">
              {{ c.title }}
            </h2>
            <div class="mt-3">
              <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full bg-(--color-main) rounded-full transition-all duration-500" :style="{ width: `${Math.min(100, c.percent)}%` }" />
              </div>
              <div class="flex items-center justify-between mt-2 text-xs text-gray-600">
                <span class="font-semibold text-gray-900">{{ formatCurrency(c.raised) }}</span>
                <span>{{ Math.round(c.percent) }}%</span>
              </div>
            </div>
          </div>
        </NuxtLink>
      </div>

      <!-- Empty (la page sera noindex côté SEO) -->
      <div v-else class="text-center py-16">
        <UIcon name="i-lucide-heart-handshake" class="size-12 text-gray-300 mx-auto mb-4" />
        <p class="text-gray-500 mb-4">{{ t('vide.message', { ville: cityName }) }}</p>
        <NuxtLink to="/recherche/cagnottes" class="text-sm text-(--color-main) hover:underline">
          {{ t('vide.voirToutes') }}
        </NuxtLink>
      </div>

      <!-- Internal linking : autres villes (boost crawl SEO) -->
      <div v-if="data?.cities && data.cities.length > 1" class="mt-16 pt-12 border-t border-gray-100">
        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {{ t('autresVilles.titre') }}
        </h2>
        <p class="text-gray-500 text-sm mb-6">
          {{ t('autresVilles.description') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <NuxtLink
            v-for="city in data.cities.filter((c) => c.slug !== villeSlug).slice(0, 30)"
            :key="city.slug"
            :to="`/cagnottes/${city.slug}`"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 no-underline transition-colors"
          >
            <span>{{ city.name }}</span>
            <span class="text-gray-400">{{ city.count }}</span>
          </NuxtLink>
        </div>
      </div>

      <!-- Footer maillage interne SEO (discret, liens texte) -->
      <p class="mt-12 pt-6 border-t border-gray-100 text-sm text-gray-500 leading-relaxed">
        <span class="font-medium text-gray-700">{{ t('maillage.voirAussi', { ville: cityName }) }}</span>
        <NuxtLink :to="`/associations/ville/${villeSlug}`" class="text-(--color-main) hover:underline ml-1">{{ t('maillage.associations') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/evenements/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.evenements') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/adhesions/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.adhesions') }}</NuxtLink>
        <span class="text-gray-400 mx-1.5">·</span>
        <NuxtLink :to="`/boutiques/${villeSlug}`" class="text-(--color-main) hover:underline">{{ t('maillage.boutiques') }}</NuxtLink>
      </p>
      </UContainer>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/pages/cagnottes/[ville].json"></i18n>
